import { useAxios } from "../useAxios"
import { useAuthStore } from "../../../stores/auth/useAuthStore"
import { IAutoTag, ITag } from "../../../interfaces/models/connection"
import { ICreateTag } from "../../../interfaces/models/tag"

export interface IConnectionsQueryParams {
    page?: number,
    pageSize?: number,
    sort?: string,
    searchValues?: ISearchValues[],
    searchByFollowers?: number,
    searchByMaxFollowers?: number,
    companyEmployeesMin?: number,
    companyEmployeesMax?: number,
    isHidden?: string,
    note?: string,
    message?: string,
    tags?: ITag[],
    excludeTags?: ITag[],
    noTags?: boolean,
    industry?: string,
    isFollowing?: string,
    contactInfo?: string,
    connectedAtFrom?: string,
    connectedAtTo?: string,
    birthDateFrom?: string,
    birthDateTo?: string,
    lastMessagedFrom?: string,
    lastMessagedTo?: string,
    conversationStatus?: string,
    onlyLast?: boolean,
    languages?: string[],
    tagCondition?: string,
    languageCondition?: string,
    isExcludeMode?: boolean,
    autoTagsArray?: IAutoTag[],
    autotagsFilter?: string,
    usersMutual?: string,
    hasJobChanges?: string,
    hasCompanyChanges?: string,
}

export interface ISearchValues {
    searchValue?: string,
    searchByName?: string,
    searchByCompany?: string,
    searchByHeadline?: string,
    searchByLocation?: string,
    searchByJobTitle?: string,
    searchByNote?: string,
}


export interface IConnectionsQuerySelectedParams extends IConnectionsQueryParams {
    connectionIds?: string []
}

export interface IApplyTagsPayload { tags: (string | ICreateTag)[] }
export interface IBulkTagsPayload { filters: IConnectionsQuerySelectedParams, tags: (string | ICreateTag)[] }
export interface IRemoveTagsPayload { tags: string[] }
export interface IExportToCSVPayload { includeTags: boolean, includeNotes: boolean, filters: IConnectionsQuerySelectedParams }
export interface ItoggleBulkHideConnectionsPayload { hideConnection: boolean, filters: IConnectionsQuerySelectedParams }
export interface IConnectionProfileId { profileId?: string }
export interface IBulkAddConnectionsToKanbanPayload {
    filters: IConnectionsQuerySelectedParams;
    kanbanIds?: string[];
}

export interface IProcessPaymentParams {
    clientSecret: string,
    currency: string,
    amount: number,
    description?: string,
    paymentIntentId?: string,
}

export interface IBulkUpdatePostTrackingPayload {
    connectionIds: string[],
    isPostTracked: boolean
}

export interface IUpdateLinkedinMutualProfilesPayload {
    publicIdentifiers: string[]
}

export const useConnectionsRequest = () => {
    const { sendRequest } = useAxios()
    const { store: { authData: { accessToken } } } = useAuthStore()

    const route = '/profiles';

    //GET
    const getConnections = (params: any, slug?: string) => {
        return sendRequest({
            slug: slug ?? 'getConnections',
            url: `${route}/v1`,
            method: 'GET',
            params
        })
    };

    const getConnectionsTotal = (params: any) => {
        return sendRequest({
            slug: 'getConnectionsTotal',
            url: `${route}/v1/total`,
            method: 'GET',
            params
        })
    };

    const getConnectionsHighlights = (params: any) => {
        return sendRequest({
            slug: 'getConnectionsHighlights',
            url: `${route}/v1/highlights`,
            method: 'POST',
            payload: params,
            displayToast: false
        })
    };

    /** Plan/trial access for Post Scanner + campaign post commenter import (no activityUrn needed). */
    const checkPostCommentersAccess = () => {
        return sendRequest({
            slug: 'checkPostCommentersAccess',
            url: `${route}/bulk/from-post/check`,
            method: 'GET',
            displayToast: false,
        })
    };

    const getConnectionByProfileId = (_id: IConnectionProfileId) => {
        return sendRequest({
            slug: 'getConnectionByProfileId',
            url: `${route}/${_id}`,
            method: 'GET'
        })
    };

    /** Profile document Mongo _id (not LinkedIn `profileId` string). Maps to GET /profiles/profile/:id. */
    const getConnectionByProfileDocumentId = (profileDocumentId: string) => {
        return sendRequest({
            slug: 'getConnectionByProfileDocumentId',
            url: `${route}/profile/${profileDocumentId}`,
            method: 'GET',
            displayToast: false
        })
    };

    const getConnectionByPublicIdentifier = (publicIdentifier: string) => {
        return sendRequest({
            slug: 'getConnectionByPublicIdentifier',
            url: `${route}/profile/publicIdentifier/${publicIdentifier}`,
            method: 'GET'
        })
    };

    const getConnectionsByPublicIdentifiers = (publicIdentifiers: string[]) => {
        return sendRequest({
            slug: 'getConnectionsByPublicIdentifiers',
            url: `${route}/publicIdentifiers`,
            method: 'POST',
            payload: { publicIdentifiers },
            displayToast: false
        })
    };


    const getMultipleConditionsFilters = (params: IConnectionsQueryParams) => {
        return sendRequest({
            slug: 'getMultipleConditionsFilters',
            url: `${route}/multipleConditionsFilters`,
            method: 'GET',
            params
        })
    };

    const getExceededDailyLimit = () => {
        return sendRequest({
            slug: 'getExceededDailyLimit',
            url: `${route}/exceeded-daily-limit`,
            method: 'GET',
            all: true
        })
    }

    const getSelectedConnectionsIds = (params: any) => {
        return sendRequest({
            slug: 'getSelectedConnectionsIds',
            url: `${route}/v1/selected`,
            method: 'GET',
            params
        })
    };

    const getKanbanWithConnections = (workspaceId: any, page?: number, limit?: number, columnId?: string, kanbanId?: string, offset?: number) => {
        const params: any = {};
        if (page !== undefined) params.page = page;
        if (limit !== undefined) params.limit = limit;
        if (columnId) params.columnId = columnId;
        if (kanbanId) params.kanbanId = kanbanId;
        if (offset !== undefined) params.offset = offset;

        return sendRequest({
            slug: 'getKanbanWithConnections',
            url: `${route}/kanban-with-connections/${workspaceId}`,
            method: 'GET',
            params
        })
    };

    const getKanbans = (workspaceId: string) => {
        return sendRequest({
            slug: 'getKanbans',
            url: `${route}/kanbans/${workspaceId}`,
            method: 'GET',
            all: true
        })
    };

    const createKanban = (payload: any) => {
        return sendRequest({
            slug: 'createKanban',
            url: `${route}/kanbans`,
            method: 'POST',
            payload,
            displayToast: true,
            displayErrorToast: true,
            all: true
        })
    };

    const getCompanyOverview = (props: any) => {
        const companyId = props.companyId
        return sendRequest({
            slug: 'getCompanyOverview',
            url: `${route}/companies/${companyId}`,
            method: 'GET'
        })
    };

    //PUT
    const updateProfileFailed = (profileId: string) => {
        return sendRequest({
            slug: 'updateProfileFailed',
            url: `${route}/${profileId}/updateFailed`,
            method: 'PUT',
            payload: {}
        })
    }

    const updateProfile = (profileId: string, profileData: any) => {
        return sendRequest({
            slug: 'updateProfile',
            url: `${route}/${profileId}/update`,
            method: 'PUT',
            payload: profileData,
            displayToast: false
        })
    }

    //PATCH
    const updateConnection = (connectionId: string, payload: any, additionalHeaders?: { [key: string]: string }) => {
        return sendRequest({
            slug: 'updateConnection',
            url: `${route}/${connectionId}`,
            method: 'PATCH',
            payload,
            ...(additionalHeaders ? { additionalHeaders } : {}),
            displayToast: false,
            displayErrorToast: false
        })
    }

    const updateConnectionColumnIdInKanban = (connectionId: string, payload: any) => {
        return sendRequest({
            slug: 'updateConnectionColumnIdInKanban',
            url: `${route}/update-connection-column/${connectionId}`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false
        })
    }

    const updateLinkedinMutualProfiles = (connectionId: string, payload: IUpdateLinkedinMutualProfilesPayload) => {
        return sendRequest({
            slug: 'updateLinkedinMutualProfiles',
            url: `${route}/${connectionId}/linkedin-mutual-profiles`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false
        })
    }

    const getConnectionAiRecommendations = (connectionId: string) => {
        return sendRequest({
            slug: `getConnectionAiRecommendations:${connectionId}`,
            url: `${route}/${connectionId}/ai-recommendations`,
            method: 'GET',
            displayToast: false,
            displayErrorToast: false
        })
    }

    const updateKanbanColumn = (kanbanId: string, payload: any) => {
        return sendRequest({
            slug: 'updateKanbanColumn',
            url: `${route}/update-kanban-column/${kanbanId}`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false
        })
    }

    const reorderKanbanColumns = (kanbanId: string, payload: any) => {
        return sendRequest({
            slug: 'reorderKanbanColumns',
            url: `${route}/reorder-kanban-columns/${kanbanId}`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false
        })
    }

    const updateKanban = (kanbanId: string, payload: any) => {
        return sendRequest({
            slug: 'updateKanban',
            url: `${route}/update-kanban/${kanbanId}`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false,
            all: true
        })
    }

    const removeConnectionFromKanban = (connectionId: any, payload: any) => {
        return sendRequest({
            slug: 'removeConnectionFromKanban',
            url: `${route}/remove-from-kanban/${connectionId}`,
            method: 'PATCH',
            payload,
            displayToast: false,
            displayErrorToast: false
        })
    }

    //POST
    const applyTagsSingleConnection = (connectionId: string, payload: IApplyTagsPayload, displayToast?: boolean) => {
        return sendRequest({
            slug: 'applyTag', //maybe add id to listen to loading completed
            url: `${route}/${connectionId}/tags`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const applyTagsToConnections = (payload: IBulkTagsPayload, displayToast?: boolean) => {
        return sendRequest({
            slug: 'bulkApplyTag', //maybe add id to listen to loading completed
            url: `${route}/bulk/tags/apply`,
            method: 'POST',
            payload,
            displayToast
        })
    }

    const addConnectionToKanban = (payload: any, displayToast?: boolean) => {
        return sendRequest({
            slug: 'addToKanban',
            url: `${route}/add-to-kanban`,
            method: 'POST',
            payload,
            displayErrorToast: false,
            displayToast: false
        })
    }

    const bulkAddConnectionsToKanban = (payload: IBulkAddConnectionsToKanbanPayload) => {
        return sendRequest({
            slug: 'bulkAddToKanban',
            url: `${route}/bulk/add-to-kanban`,
            method: 'POST',
            payload,
        })
    }

    const addColumnToKanban = (payload: any) => {
        return sendRequest({
            slug: 'addColumnToKanban',
            url: `${route}/add-column-to-kanban`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const getEnrichedConnections = (payload: any) => {
        return sendRequest({
            slug: 'getEnrichedConnections',
            url: `${route}/bulk/enrich`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const calculateEnrichment = (payload: any) => {
        return sendRequest({
            slug: 'calculateEnrichment',
            url: `${route}/bulk/calculate-enrichment-price`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const processEnrichmentPayment = (params: IProcessPaymentParams) => {
        return sendRequest({
            slug: 'processEnrichmentPayment',
            url: `${route}/enrichment-payment/process`,
            method: 'POST',
            payload: {
                paymentIntentId: params.paymentIntentId,
                clientSecret: params.clientSecret,
                currency: params.currency,
                amount: params.amount,
                description: params.description,
            },
        });
    }

    const getEnrichedTransactionByClientSecret = (payload: any) => {
        return sendRequest({
            slug: 'getEnrichedTransactionByClientSecret',
            url: `${route}/enriched-transaction`,
            method: 'POST',
            payload,
            displayToast: false
        });
    }

    const getEnrichedTransactionById = (payload: any) => {
        return sendRequest({
            slug: 'getEnrichedTransactionById',
            url: `${route}/enriched-transaction-by-id`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const estimateEnrichmentCredits = (payload: any) => {
        return sendRequest({
            slug: 'estimateEnrichmentCredits',
            url: `${route}/bulk/estimate-enrichment-credits`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const enrichConnections = (payload: any) => {
        return sendRequest({
            slug: 'enrichConnections',
            url: `${route}/enrich-connections`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const buyCreditsPaymentIntent = (payload: any) => {
        return sendRequest({
            slug: 'buyCreditsPaymentIntent',
            url: `${route}/buy-credits-payment-intent`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const buyCreditsPaymentIntentWithoutEnrichedTransaction = (payload: any) => {
        return sendRequest({
            slug: 'buyCreditsPaymentIntentWithoutEnrichedTransaction',
            url: `${route}/buy-credits-payment-intent-without-enriched-transaction`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const buyCreditsInvoice = (payload: any) => {
        return sendRequest({
            slug: 'buyCreditsInvoice',
            url: `${route}/buy-credits-invoice`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const buyCreditsInvoiceWithoutEnrichedTransaction = (payload: any) => {
        return sendRequest({
            slug: 'buyCreditsInvoiceWithoutEnrichedTransaction',
            url: `${route}/buy-credits-invoice-without-enriched-transaction`,
            method: 'POST',
            payload,    
            displayToast: false
        })
    }

    const addToLeadDelta = (payload: any) => {
        return sendRequest({
            slug: 'addToLeadDelta',
            url: `${route}/new`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const addToLeadDeltaBulk = (payload: any) => {
        return sendRequest({
            slug: 'addToLeadDeltaBulk',
            url: `${route}/bulk/new`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    //DELETE
    const removeTagsSingleConnection = (connectionId: string, payload: IRemoveTagsPayload, displayToast?: boolean) => {
        return sendRequest({
            slug: 'removeTag', //maybe add id to listen to loading completed
            url: `${route}/${connectionId}/tags`,
            method: 'DELETE',
            payload,
            displayToast: false,
        })
    }

    const removeTagsFromConnections = (payload: IBulkTagsPayload, displayToast?: boolean) => {
        return sendRequest({
            slug: 'bulkRemoveTags',
            url: `${route}/bulk/tags/remove`,
            method: 'DELETE',
            payload,
            displayToast
        })
    }

    const removeConnection = (connectionId: string, removeFromLD?: boolean, displayToast?: boolean) => {
        return sendRequest({
            slug: 'removeConnection',
            url: `${route}/${connectionId}${removeFromLD ? `?removeConnection=true` : ''}`,
            method: 'DELETE',
            ...(!!displayToast && { displayToast }),
            all: true
        })
    }

    const removeConnections = (payload: any, removeFromLD?: boolean, allSelected?: boolean, displayToast?: boolean) => {
        return sendRequest({
            slug: 'removeConnections',
            url: `${route}/bulk/disconnect/${removeFromLD ? `?removeConnection=true&allSelectedConnection=${allSelected}` : ''}`,
            method: 'DELETE',
            payload,
            ...(!!displayToast && { displayToast }),
            all: true
        })
    }

    const removeKanban = (kanbanId: string) => {
        return sendRequest({
            slug: 'removeKanban',
            url: `${route}/remove-kanban/${kanbanId}`,
            method: 'DELETE',
            displayToast: true,
            displayErrorToast: true,
            all: true
        })
    }

    const removeKanbanColumn = (kanbanId: string, payload: any) => {
        return sendRequest({
            slug: 'removeKanbanColumn',
            url: `${route}/remove-kanban-column/${kanbanId}`,
            method: 'DELETE',
            payload,
            displayToast: true,
            displayErrorToast: true
        })
    }

    // other
    const exportToCSV = (payload: IExportToCSVPayload) => {
        return sendRequest({
            slug: 'exportToCSV',
            url: `${route}/download/csv`,
            method: 'POST',
            payload,
            all: true
        })
    }

    const bulkImportToHubspot = (payload: any) => {
        const { exportType } = payload;

        const requestBody: Record<string, any> = {
            connectionIds: Array.isArray(payload.connectionIds) ? payload.connectionIds : [],
            type: exportType,
        };

        if (payload.exportAll) {
            requestBody.exportAll = true;
        }

        if (exportType === 'list') {
            requestBody.listId = payload.listId;
            if (payload.createIfNotExists !== undefined) {
                requestBody.createIfNotExists = payload.createIfNotExists;
            }
        } else if (exportType === 'workflow') {
            requestBody.workflowId = payload.workflowId;
            if (payload.createIfNotExists !== undefined) {
                requestBody.createIfNotExists = payload.createIfNotExists;
            }
        } else if (exportType === 'deals') {
            requestBody.dealStage = payload.dealStage;
            if (payload.dealName) requestBody.dealName = payload.dealName;
            if (payload.pipelineId) requestBody.pipelineId = payload.pipelineId;
            if (payload.amount !== undefined && payload.amount !== null) {
                requestBody.amount = payload.amount;
            }
            if (payload.createContactIfNotExists !== undefined) {
                requestBody.createContactIfNotExists = payload.createContactIfNotExists;
            }
        }

        return sendRequest({
            slug: 'enqueueHubspotExport',
            url: `${route}/bulk/export-hubspot`,
            method: 'POST',
            payload: requestBody,
            all: true
        })
    }

    const toggleBulkHideConnections = (payload: ItoggleBulkHideConnectionsPayload) => {
        return sendRequest({
            slug: 'toggleBulkHideConnections',
            url: `${route}/bulk/hide`,
            method: 'PATCH',
            payload,
            all: true,
            displayToast: false,
            displayErrorToast: false
        })
    }
    
    const scheduleConnectionsRemoval = (payload: any) => {
        return sendRequest({
            slug: 'scheduleConnectionsRemoval',
            url: `${route}/bulk/scheduleConnectionsRemoval`,
            method: 'PATCH',
            payload,
        })
    }

    const cancelConnectionsRemoval = () => {
        return sendRequest({
            slug: 'cancelConnectionsRemoval',
            url: `${route}/bulk/cancelConnectionsRemoval`,
            method: 'PATCH',
            displayToast: false
        })
    }

    const connectionsRemovalFinished = (payload: any) => {
        return sendRequest({
            slug: 'connectionsRemovalFinished',
            url: `${route}/bulk/connectionsRemovalFinished`,
            method: 'PATCH',
            payload,
            displayToast: false,
            all: true
        })
    }

    const getNextConnectionsScheduledForRemoval = (params?: any) => {
        return sendRequest({
            slug: 'getNextConnectionsScheduledForRemoval',
            url: `${route}/v1/scheduledForRemoval`,
            method: 'GET',
            params
        })
    };

        const bulkUpdateConnections = (payload: any) => {
        return sendRequest({
            slug: 'bulkUpdateConnections',
            url: `${route}/bulk/connections/update`,
            method: 'POST',
            payload,
            displayToast: false,
            all: true
        })
    }

    const bulkUpdatePostTrackingStatus = (payload: IBulkUpdatePostTrackingPayload) => {
        return sendRequest({
            slug: 'bulkUpdatePostTrackingStatus',
            url: `${route}/bulk/post-tracking`,
            method: 'PATCH',
            payload,
            displayToast: false
        })
    }

    const updateCustomFieldValue = (connectionId: string, payload: { key: string, value: any }) => {
        return sendRequest({
            slug: 'updateCustomFieldValue',
            url: `${route}/${connectionId}/custom-field-value`,
            method: 'POST',
            payload,
            displayToast: false,
            all: true
        })
    }

    const createCustomFieldDefinition = (payload: { label: string, type: string, key?: string, metadata?: any }) => {
        return sendRequest({
            slug: 'createCustomFieldDefinition',
            url: `${route}/custom-field-definition`,
            method: 'POST',
            payload,
            displayToast: false,
            all: true
        })
    }

    const getCustomFieldDefinitions = () => {
        return sendRequest({
            slug: 'getCustomFieldDefinitions',
            url: `${route}/custom-field-definitions`,
            method: 'GET',
            displayToast: false
        })
    }

    const deleteCustomFieldDefinitions = (payload: { keys: string[] }) => {
        return sendRequest({
            slug: 'deleteCustomFieldDefinitions',
            url: `${route}/custom-field-definitions/delete`,
            method: 'POST',
            payload,
            displayToast: false,
            all: true
        })
    }

    return {
        getConnections,
        getConnectionsTotal,
        getConnectionsHighlights,
        getMultipleConditionsFilters,
        applyTagsSingleConnection,
        removeTagsSingleConnection,
        getConnectionByProfileId,
        getConnectionByProfileDocumentId,
        applyTagsToConnections,
        removeTagsFromConnections,
        getExceededDailyLimit,
        updateProfileFailed,
        updateProfile,
        updateConnection,
        exportToCSV,
        bulkImportToHubspot,
        toggleBulkHideConnections,
        removeConnection,
        removeConnections,
        scheduleConnectionsRemoval,
        cancelConnectionsRemoval,
        connectionsRemovalFinished,
        getNextConnectionsScheduledForRemoval,
        getSelectedConnectionsIds,
        addConnectionToKanban,
        getKanbanWithConnections,
        updateConnectionColumnIdInKanban,
        addColumnToKanban,
        updateKanbanColumn,
        updateKanban,
        removeConnectionFromKanban,
        bulkAddConnectionsToKanban,
        reorderKanbanColumns,
        removeKanban,
        removeKanbanColumn,
        getCompanyOverview,
        getEnrichedConnections,
        calculateEnrichment,
        processEnrichmentPayment,
        getEnrichedTransactionByClientSecret,
        estimateEnrichmentCredits,
        enrichConnections,
        getEnrichedTransactionById,
        buyCreditsPaymentIntent,
        buyCreditsPaymentIntentWithoutEnrichedTransaction,
        buyCreditsInvoice,
        buyCreditsInvoiceWithoutEnrichedTransaction,
        addToLeadDelta,
        addToLeadDeltaBulk,
        bulkUpdateConnections,
        bulkUpdatePostTrackingStatus,
        getConnectionByPublicIdentifier,
        getConnectionsByPublicIdentifiers,
        updateLinkedinMutualProfiles,
        getConnectionAiRecommendations,
        updateCustomFieldValue,
        createCustomFieldDefinition,
        getCustomFieldDefinitions,
        deleteCustomFieldDefinitions,
        getKanbans,
        createKanban,
        checkPostCommentersAccess,
    };
}